import { useEffect, useRef, useState } from 'react';
import { Terminal, X, Trash2, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import { SANDBOX_MODE, subscribeSandbox, clearSandboxEntries } from '../config/sandbox';

// Floating in-app console for the sandbox. Subscribes to every intercepted
// POST and shows endpoint + timestamp + expandable JSON. Only renders when
// VITE_SANDBOX_MODE=true.

function formatTime(d) {
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  return `${hh}:${mm}:${ss}.${ms}`;
}

function EntryCard({ entry }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const json = JSON.stringify(entry.payload, null, 2);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // ignore — clipboard may be blocked in non-https local
    }
  };

  return (
    <div className="border border-sky-200 rounded-md bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-sky-50 transition-colors"
      >
        {open
          ? <ChevronDown size={14} className="text-sky-700 flex-shrink-0" />
          : <ChevronRight size={14} className="text-sky-700 flex-shrink-0" />}
        <code className="text-xs font-mono font-semibold text-sky-900 truncate flex-1">
          {entry.label}
        </code>
        <span className="text-[10px] text-neutral-500 font-mono flex-shrink-0">
          {formatTime(entry.timestamp)}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="p-1 rounded hover:bg-sky-100 text-sky-700 flex-shrink-0"
          title="Copy JSON"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
        </button>
      </button>
      {open && (
        <pre className="text-[11px] font-mono px-3 py-2 bg-neutral-900 text-neutral-100 overflow-x-auto max-h-72 overflow-y-auto m-0">
          {json}
        </pre>
      )}
    </div>
  );
}

export default function SandboxConsole() {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [seenCount, setSeenCount] = useState(0);
  const listRef = useRef(null);

  useEffect(() => {
    if (!SANDBOX_MODE) return undefined;
    const unsub = subscribeSandbox(setEntries);
    return unsub;
  }, []);

  // When the panel is open, anything currently visible is "seen".
  useEffect(() => {
    if (open) setSeenCount(entries.length);
  }, [open, entries.length]);

  if (!SANDBOX_MODE) return null;

  const unseen = Math.max(0, entries.length - seenCount);

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-3 py-2 rounded-full shadow-lg text-sm font-semibold transition-colors"
          title="Open sandbox console"
        >
          <Terminal size={16} />
          Sandbox
          <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-mono ${
            unseen > 0 ? 'bg-amber-300 text-amber-900' : 'bg-sky-800 text-sky-100'
          }`}>
            {unseen > 0 ? `+${unseen}` : entries.length}
          </span>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-4 right-4 z-50 w-[420px] max-w-[calc(100vw-2rem)] max-h-[70vh] flex flex-col bg-white border border-sky-300 rounded-lg shadow-2xl"
        >
          <div className="flex items-center justify-between gap-2 px-3 py-2 bg-sky-600 text-white rounded-t-lg">
            <div className="flex items-center gap-2 font-semibold text-sm">
              <Terminal size={16} />
              Sandbox Console
              <span className="text-xs font-mono opacity-90">
                ({entries.length} intercepted)
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={clearSandboxEntries}
                className="p-1.5 rounded hover:bg-sky-700 transition-colors"
                title="Clear log"
                disabled={entries.length === 0}
              >
                <Trash2 size={14} />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded hover:bg-sky-700 transition-colors"
                title="Close"
              >
                <X size={14} />
              </button>
            </div>
          </div>
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto p-2 space-y-1.5 bg-neutral-50"
          >
            {entries.length === 0 ? (
              <div className="text-center text-xs text-neutral-500 py-8 px-4">
                No intercepted POSTs yet.
                <br />
                Take an action that writes to the DB (Push to ledger, Save resolution, etc.) and the payload will appear here.
              </div>
            ) : (
              entries.map(entry => <EntryCard key={entry.id} entry={entry} />)
            )}
          </div>
        </div>
      )}
    </>
  );
}
